import { useMedia, YStack } from 'tamagui'
import { Slider } from '@components/Slider'
import { ProductCard } from './ProductCard'
import { DefaultSectionTitle } from '../DefaultSectionTitle'
import { useCallback, useMemo, useState } from 'react'
import { IEnrollment } from '@dtos/Enrollment'
import { useGetMyProductsConfig } from '@hooks/useMembersConfig'
import { useWindowDimensions } from 'react-native'
import { CARD_DIMENSIONS } from '@constants/productCardDimension'
import { useMediaSize } from '@hooks/useMediaSize'
import { EnrollmentContentsModal } from '../EnrollmentContentsModal'
import { ContinueCard } from '../Continue/ContinueCard'
import { InView } from '@components/IntersectionObserver'

interface IMyProductSectionProps {
  data: IEnrollment[] | null
  totalProductsNumber: number
  handleLoadMoreProducts: (offset: number) => Promise<void>
  showProductName: boolean
  hasMore?: boolean
}

export function MyProductsSection({
  data,
  totalProductsNumber,
  handleLoadMoreProducts,
  showProductName,
  hasMore = true,
}: IMyProductSectionProps) {
  const [openModal, setOpenModal] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const { data: myProductsConfig } = useGetMyProductsConfig()
  const isHorizontal = myProductsConfig?.cardFormat
    ? myProductsConfig.cardFormat === 'horizontal'
    : true
  const media = useMedia()
  const { width: windowWidth } = useWindowDimensions()
  const sizes = useMediaSize()

  const containerPadding = media.gtSm ? 48 : 16
  const spaceBetweenCards = media.gtSm ? 20 : 16

  const slidesPerView = useCallback(() => {
    const orientation = isHorizontal ? 'horizontal' : 'vertical'
    const cardWidth =
      CARD_DIMENSIONS[orientation][sizes] || CARD_DIMENSIONS[orientation].xs
    return (
      (windowWidth - containerPadding) / (cardWidth.width + spaceBetweenCards)
    )
  }, [containerPadding, isHorizontal, sizes, spaceBetweenCards, windowWidth])

  const shouldDisplaySeeAllButton = useMemo(() => {
    return data && data.length > slidesPerView()
  }, [data, slidesPerView])

  const canDisplaySliderControls = useMemo(() => {
    return media.gtSm && !!data && data.length > slidesPerView()
  }, [media.gtSm, data, slidesPerView])
  const shouldDisplaySliderControls = media.gtMd && isHovered

  const handleButtonPress = useCallback(() => {
    setOpenModal(true)
  }, [])

  // O Slider já respeita hasMore; o sentinela do modal "Ver tudo" não, e
  // seguia pedindo página depois do fim da lista.
  const handleLoadMore = useCallback(() => {
    if (!hasMore) return
    handleLoadMoreProducts(data?.length || 0)
  }, [data?.length, handleLoadMoreProducts, hasMore])

  const renderModalCards = useMemo(() => {
    if (!data) return []
    return data?.map((item: IEnrollment, index: number) => {
      if (index === data.length - 1) {
        return (
          <InView
            key={item.id}
            onChange={(inView: boolean) => inView && handleLoadMore()}
          >
            <ContinueCard
              key={item.id}
              cardId={item.id.toString()}
              showProductName={showProductName}
              shouldExtendCard={false}
              productTitle={item.schoolProduct.title}
              isModalVersion={true}
              closeModal={() => setOpenModal(false)}
              customThumbnail={item.schoolProduct.logo}
              progressCard={item.progressCard ?? undefined}
              usedAsProduct={true}
              firstLesson={
                item.schoolProduct.courses?.[0]?.lessons.nodes?.[0] ?? undefined
              }
              course={item.schoolProduct.courses?.[0] ?? undefined}
              enrollmentId={item.id.toString()}
            />
          </InView>
        )
      } else {
        return (
          <ContinueCard
            key={item.id}
            cardId={item.id.toString()}
            showProductName={showProductName}
            shouldExtendCard={false}
            productTitle={item.schoolProduct.title}
            isModalVersion={true}
            closeModal={() => setOpenModal(false)}
            customThumbnail={item.schoolProduct.logo}
            progressCard={item.progressCard ?? undefined}
            usedAsProduct={true}
            firstLesson={
              item.schoolProduct.courses?.[0]?.lessons.nodes?.[0] ?? undefined
            }
            course={item.schoolProduct.courses?.[0] ?? undefined}
            enrollmentId={item.id.toString()}
          />
        )
      }
    })
  }, [data, showProductName, handleLoadMore])

  return (
    <YStack
      testID="my-products-section"
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
    >
      <Slider
        slidesTotalNumber={totalProductsNumber}
        slidesPerView={slidesPerView()}
        displaySliderControls={canDisplaySliderControls}
        slidesSpacing={spaceBetweenCards}
        navigationControlVariation="dots"
        loadMoreContent={handleLoadMore}
        hasMore={hasMore}
        sliderTitle={
          <DefaultSectionTitle
            title="Meus produtos"
            onButtonPress={handleButtonPress}
            isHoveringSection={isHovered}
            showButton={shouldDisplaySeeAllButton ?? false}
          />
        }
        displaySliderControlsHorizontal={shouldDisplaySliderControls}
        contentInset={containerPadding}
      >
        {data?.map((item: IEnrollment) => {
          return (
            <ProductCard
              key={item.id}
              hasAuxiliarTitle={showProductName}
              isHorizontal={isHorizontal}
              data={{
                id: item.id.toString(),
                enrollmentId: item.id.toString(),
                courseId: item.schoolProduct.courses?.[0]?.id.toString(),
                activatedAt: item.activatedAt,
                availableUntil: item.availableUntil,
                progress: item.progressCard?.progress,
                totalLessons: item.progressCard?.total,
                completedLessons: item.progressCard?.completed,
                title: item.schoolProduct.title,
                logo: item.schoolProduct.logo,
                logoVertical: item.schoolProduct.logoVertical,
                kind: item.schoolProduct.courses?.[0]?.kind,
                pathId: item.schoolProduct.path?.id,
                hasLiveSoon: item.livemeetRoomsAvailable.length > 0,
                courseContentId:
                  Number(
                    item.schoolProduct.courses?.[0]?.lessons?.nodes?.[0]
                      ?.courseContentId,
                  ) ?? null,
                firstLesson:
                  item.schoolProduct.courses?.[0]?.lessons?.nodes?.[0] ?? null,
              }}
            />
          )
        })}
      </Slider>
      <EnrollmentContentsModal
        title="Meus produtos"
        open={openModal}
        onClose={() => setOpenModal(false)}
        lessonCards={renderModalCards}
      />
    </YStack>
  )
}
